"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowLeft, Menu, X } from "lucide-react";
import { useState } from "react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import LocaleSwitcher from "@/components/site/LocaleSwitcher";
import { useAppLocale, useT } from "@/i18n/context";

export function SiteHeader() {
  const t = useT();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/", label: t("common.nav.home", "خانه") },
    { to: "/features", label: t("common.nav.featuresList", "ویژگی‌ها") },
    { to: "/pricing", label: t("common.nav.pricingPlans", "قیمت‌گذاری") },
    { to: "/about", label: t("common.nav.about", "درباره ما") },
    { to: "/blog", label: t("common.nav.blog", "بلاگ") },
    { to: "/changelog", label: t("common.nav.changelog", "تغییرات") },
    { to: "/contact", label: t("common.nav.contactShort", "تماس") },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-6">
        <Link href="/" className="flex items-center gap-3">
          <BrandMark size={36} />
          <div className="hidden flex-col leading-none sm:flex">
            <span className="text-[14px] font-black tracking-tight text-foreground">
              {t("common.brandName", "ترندی اسمارت")}
            </span>
            <span className="mt-1 text-[9px] font-bold tracking-[0.25em] text-primary-main">
              {t("common.brandTagline", "TRENDI · SMART")}
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {nav.map((n) => (
            <Link
              key={n.to}
              href={n.to}
              className="rounded-lg px-3 py-2 text-[13px] font-bold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle variant="floating" />
          <LocaleSwitcher className="hidden border-border bg-muted text-foreground hover:border-primary-main/40 sm:inline-flex" />
          <Link
            href="/login"
            className="hidden rounded-lg bg-primary-main px-3.5 py-2 text-[12.5px] font-black text-white shadow-sm shadow-primary-main/30 transition-colors hover:bg-primary-400 sm:inline-flex"
          >
            {t("common.auth.login", "ورود")}
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground lg:hidden"
            aria-label={t("common.menu", "منو")}
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t border-border/60 bg-background/95 backdrop-blur-xl lg:hidden"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-3">
            {nav.map((n) => (
              <Link
                key={n.to}
                href={n.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-[14px] font-bold text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {n.label}
              </Link>
            ))}
            <Link
              href="/login"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-lg bg-primary-main px-3 py-2.5 text-center text-[13px] font-black text-white"
            >
              {t("common.auth.loginToAccount", "ورود به حساب")}
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
}

export function SiteFooter() {
  const t = useT();
  const locale = useAppLocale();
  const year = new Intl.DateTimeFormat(locale === "fa" ? "fa-IR" : "en-US", {
    year: "numeric",
  }).format(new Date());

  return (
    <footer className="border-t border-border/60 bg-card/40">
      <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <BrandMark size={32} animated={false} />
            <span className="text-[14px] font-black text-foreground">
              {t("common.brandName", "ترندی اسمارت")}
            </span>
          </div>
          <p className="mt-3 text-[12.5px] leading-relaxed text-muted-foreground">
            {t(
              "footer.tagline",
              "پلتفرم هوشمند رشد کسب‌وکار آنلاین — اتوماسیون، آنالیز و هوش مصنوعی در یک جا."
            )}
          </p>
        </div>
        <FooterCol
          title={t("footer.columns.product", "محصول")}
          links={[
            { to: "/features", label: t("common.nav.featuresList", "ویژگی‌ها") },
            { to: "/pricing", label: t("common.nav.pricingPlans", "قیمت‌گذاری") },
            { to: "/changelog", label: t("common.nav.changelog", "تغییرات") },
            { to: "/blog", label: t("common.nav.blog", "بلاگ") },
          ]}
        />
        <FooterCol
          title={t("footer.columns.company", "شرکت")}
          links={[
            { to: "/about", label: t("common.nav.about", "درباره ما") },
            { to: "/customers", label: t("common.nav.customers", "مشتریان") },
            { to: "/case-studies", label: t("common.nav.caseStudies", "نمونه‌کارها") },
            { to: "/contact", label: t("common.nav.contact", "تماس با ما") },
          ]}
        />
        <FooterCol
          title={t("footer.columns.resources", "منابع")}
          links={[
            { to: "/integrations", label: t("common.nav.integrations", "یکپارچگی‌ها") },
            { to: "/security", label: t("common.nav.security", "امنیت") },
            { to: "/status", label: t("common.nav.status", "وضعیت سرویس") },
            // نسخه‌ی انگلیسی صفحه‌های حقوقی جداست (همان‌هایی که در تنظیمات اپ Meta ثبت می‌شوند).
            { to: locale === "en" ? "/privacy-policy" : "/privacy", label: t("common.nav.privacy", "حریم خصوصی") },
            { to: locale === "en" ? "/terms-of-service" : "/terms", label: t("common.nav.terms", "قوانین") },
            ...(locale === "en" ? [{ to: "/data-deletion", label: "Data Deletion" }] : []),
          ]}
        />
      </div>
      <div className="border-t border-border/60 px-6 py-5 text-center text-[11.5px] text-muted-foreground">
        {t("footer.copyright", "© {year} ترندی اسمارت — تمام حقوق محفوظ است.", { year })}
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <h4 className="mb-3 text-[12px] font-black tracking-wider text-foreground">{title}</h4>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              href={l.to}
              className="text-[12.5px] text-muted-foreground transition-colors hover:text-primary-main"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 -z-0 overflow-hidden">
        <motion.div
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 14, repeat: Infinity }}
          className="absolute -right-40 top-0 h-[520px] w-[520px] rounded-full bg-primary-main/10 blur-[140px]"
        />
        <motion.div
          animate={{ scale: [1.15, 1, 1.15] }}
          transition={{ duration: 16, repeat: Infinity }}
          className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-secondary-main/10 blur-[140px]"
        />
      </div>
      <div className="relative z-10 flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex-1">{children}</main>
        <SiteFooter />
      </div>
    </div>
  );
}

export function BackToHome() {
  const t = useT();

  return (
    <Link
      href="/"
      className="group inline-flex items-center gap-1.5 text-[12.5px] font-bold text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-0.5" />
      {t("common.backToHome", "بازگشت به خانه")}
    </Link>
  );
}
